import { Empty, Flex, HitCounter, Loading, Space } from '@/components';
import { useCommentList } from '@/features/comment/hooks/comment';
import { useSiderCommentFilterStorage } from '@/features/comment/hooks/local-storage';
import { useNavigateToPinpoint } from '@/features/comment/hooks/navigate-to-pinpoint';
import { useCommentStackSiderView } from '@/features/comment/hooks/stack-sider-view';
import type { CommentFilterParams } from '@/features/comment/models/search';
import { getQueryParamsByField } from '@/features/comment/utils/activity-tab-mapping-by-pipoint-field';
import {
  activeKeyQueryKeys,
  shareBySheetKeys,
} from '@/features/project/_utils/search-query-states';
import { type FC, useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useSearchParams } from 'react-router';
import invariant from 'tiny-invariant';
import type { z } from 'zod';
import type { CommentOrderingType } from '../../../models/comment';
import { CommentCard } from './CommentCard';
import { CommentSiderListFilter } from './CommentSiderListFilter';
import { CommentSiderListSorter } from './CommentSiderListSorter';

export const CommentSiderList: FC = () => {
  const { t } = useTranslation();

  const stackSiderView = useCommentStackSiderView();

  const siderConfig = stackSiderView.lastItem?.config;

  invariant(siderConfig?.viewType === 'comment-list', t('unreachable-code'));

  const { lastSearched, setSearchHistoryStorage } =
    useSiderCommentFilterStorage();
  const navigateToPinpoint = useNavigateToPinpoint();

  const [searchParams] = useSearchParams();
  const projectPageCurrentActiveKey = (searchParams.get(
    shareBySheetKeys.Enum.activeKey,
  ) ?? activeKeyQueryKeys.Enum['basic-information']) as z.infer<
    typeof activeKeyQueryKeys
  >;

  const [commentsOrdering, setCommentsOrdering] = useState<CommentOrderingType>(
    lastSearched?.ordering ?? '-replies_updated_at',
  );

  const [commentsFilterParams, setCommentsFilterParams] =
    useState<CommentFilterParams>({
      publishType: lastSearched?.publishType ?? null,
      resolved: lastSearched?.resolved ?? null,
      readStatus: lastSearched?.readStatus ?? null,
      isCurrentActiveTabOnly: lastSearched?.isCurrentActiveTabOnly ?? false,
      pinpointKey: null,
    });

  const { data: commentsData, isPending: isCommentsLoading } = useCommentList({
    params: {
      ...commentsFilterParams,
      ...(siderConfig.pinpointKey && { pinpointKey: siderConfig.pinpointKey }),
      key: 'project',
      objectId:
        stackSiderView.target.type === 'project'
          ? stackSiderView.target.projectId
          : '',
      ordering: commentsOrdering,
      all: true,
    },
  });

  const handleRemoveFilter = (key: keyof CommentFilterParams) => {
    setCommentsFilterParams((prevFilters) => {
      const newFilters = { ...prevFilters };
      delete newFilters[key];
      return newFilters;
    });

    setSearchHistoryStorage({
      ...lastSearched,
      [key]: undefined,
    });

    if (key === 'pinpointKey') {
      stackSiderView.updateLastItem({
        config: {
          ...siderConfig,
          pinpointKey: null,
          pinpoint: undefined,
        },
      });
    }
  };

  const commentList = useMemo(() => {
    const list = siderConfig.pinpoint
      ? commentsData?.results.filter(
          (comment) => comment.pinpoint?.field === siderConfig.pinpoint?.field,
        )
      : commentsData?.results;

    if (!commentsFilterParams.isCurrentActiveTabOnly) {
      return list;
    }

    const filteredList = list?.filter(({ pinpoint }) => {
      // 事業へのコメントは除外
      if (!pinpoint) {
        return false;
      }
      const queryParams = getQueryParamsByField(pinpoint);
      return projectPageCurrentActiveKey === queryParams?.activeKey;
    });

    return filteredList;
  }, [
    commentsData?.results,
    siderConfig.pinpoint,
    commentsFilterParams.isCurrentActiveTabOnly,
    projectPageCurrentActiveKey,
  ]);

  return (
    <Flex vertical gap={8}>
      <CommentSiderListFilter
        commentsFilterParams={commentsFilterParams}
        onRemoveFilter={handleRemoveFilter}
      />
      <Flex justify="space-between" style={{ height: 32 }}>
        <Space size={2}>
          <HitCounter count={commentList?.length ?? 0} label={t('relevant')} />
        </Space>

        <CommentSiderListSorter
          commentsOrdering={commentsOrdering}
          setCommentsOrdering={setCommentsOrdering}
        />
      </Flex>

      {commentList?.length ? (
        <Flex vertical gap={12}>
          {commentList.map((comment) => (
            <CommentCard
              key={comment.id}
              comment={comment}
              onClick={() => {
                if (comment.pinpoint) {
                  navigateToPinpoint({
                    objectId: comment.objectId,
                    commentId: comment.id,
                    pinpoint: comment.pinpoint,
                  });
                }

                stackSiderView.updateLastItem({
                  config: siderConfig,
                });

                stackSiderView.push({
                  payload: {
                    config: {
                      viewType: 'comment-details',
                      id: comment.id,
                      target: siderConfig.target,
                    },
                  },
                  target: stackSiderView.target,
                });
              }}
            />
          ))}
        </Flex>
      ) : isCommentsLoading ? (
        <Loading />
      ) : (
        <Empty />
      )}
    </Flex>
  );
};
